/**
 * SFrame207 — Identify Tax Filings: planner-facing filing cards vs guided five-task hub.
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, FileUp, Pencil, Trash2 } from 'lucide-react';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import { useSettlement } from '../../contexts/SettlementContext';
import {
  countTaxGuidedSubstepsDone,
  getGuidedTaxRowCompletion,
  syncPlannerFilingCardsToTaxSteps,
  syncDt4GuidedFromChildren,
  TAX_GUIDED_TOTAL,
} from './debts/taxFilingsGuided';

const FRAME_ID = 'sframe-207';

type PlannerUpload = { name: string; dataUrl?: string };

export type PlannerFilingCardRow = {
  id: string;
  formTitle: string;
  description: string;
  filingStatus: string;
  summary: string;
  uploads: PlannerUpload[];
  cardComplete: boolean;
};

function defaultPlannerCards(): PlannerFilingCardRow[] {
  return [
    {
      id: 'pf-1040',
      formTitle: 'Form 1040 (Final Individual Return)',
      description:
        'File the final personal income tax return covering income through date of death and any medical or funeral deductions allowed.',
      filingStatus: 'Not started',
      summary: '',
      uploads: [],
      cardComplete: false,
    },
    {
      id: 'pf-1041',
      formTitle: 'Form 1041 (Estate Income Tax)',
      description:
        'Report fiduciary income earned by the estate after death; issue K-1s to beneficiaries as required.',
      filingStatus: 'Not started',
      summary: '',
      uploads: [],
      cardComplete: false,
    },
    {
      id: 'pf-706',
      formTitle: 'Form 706 (Federal Estate Tax)',
      description:
        'Required when the taxable estate exceeds the federal exemption or when portability is elected.',
      filingStatus: 'Not started',
      summary: '',
      uploads: [],
      cardComplete: false,
    },
  ];
}

function extractPlannerTaxDocsCount(planner239: Record<string, unknown> | null): number {
  if (!planner239) return 0;
  const tp = planner239.taskProgress as Record<string, unknown> | undefined;
  const taxBlock = tp?.tax_documents as { answers?: { documents?: unknown[] } } | undefined;
  const arr = taxBlock?.answers?.documents ?? (planner239 as { documents?: unknown[] }).documents;
  return Array.isArray(arr) ? arr.length : 0;
}

async function loadSmallFilePayload(file: File): Promise<PlannerUpload> {
  const maxBytes = 500_000;
  if (file.size > maxBytes) return { name: file.name };
  return await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () =>
      resolve({ name: file.name, dataUrl: typeof reader.result === 'string' ? reader.result : undefined });
    reader.readAsDataURL(file);
  });
}

export default function SFrame207() {
  const navigate = useNavigate();
  const location = useLocation();
  const { estateId } = useParams<{ estateId: string }>();
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData, loadPlannerData, updateStepProgress, error } = useSettlementSession(
    estateId ?? null,
    { readOnly: isArchived }
  );

  const [plannerMode, setPlannerMode] = useState(false);
  const [guidedDone, setGuidedDone] = useState(0);
  const [guidedRowDone, setGuidedRowDone] = useState<Record<string, boolean>>({});
  const [cards, setCards] = useState<PlannerFilingCardRow[]>(defaultPlannerCards);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<PlannerFilingCardRow> | null>(null);

  const refreshGuided = useCallback(async () => {
    if (!estateId) return;
    const [n, perRow] = await Promise.all([
      countTaxGuidedSubstepsDone(loadFrameData),
      getGuidedTaxRowCompletion(loadFrameData),
    ]);
    setGuidedDone(n);
    setGuidedRowDone(perRow);
  }, [estateId, loadFrameData]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!estateId) return;
      const p239 = (await loadPlannerData('frame-239')) as Record<string, unknown> | null;
      const plannerTaxCount = extractPlannerTaxDocsCount(p239);
      const inferredPlanner = plannerTaxCount > 0;

      const saved = await loadFrameData(FRAME_ID);
      const d = (saved?.data || {}) as Record<string, unknown>;
      const persistedCards = d.plannerFilings as PlannerFilingCardRow[] | undefined;

      const mode =
        typeof d.plannerModeUx === 'boolean'
          ? (d.plannerModeUx as boolean)
          : inferredPlanner;

      const initialCards =
        mode && persistedCards && persistedCards.length > 0 ? persistedCards : mode ? defaultPlannerCards() : [];

      if (!cancelled) {
        setPlannerMode(mode);
        if (initialCards.length) setCards(initialCards);

        if (typeof d.plannerModeUx !== 'boolean' && !isArchived) {
          await saveFrameData(FRAME_ID, {
            ...d,
            plannerModeUx: mode,
            ...(mode && (!persistedCards || persistedCards.length === 0)
              ? { plannerFilings: defaultPlannerCards() }
              : {}),
          });
        }
      }

      if (cancelled) return;

      if (mode) {
        const toSync =
          persistedCards && persistedCards.length > 0 ? persistedCards : defaultPlannerCards();
        await syncPlannerFilingCardsToTaxSteps(updateStepProgress, toSync);
      } else {
        await syncDt4GuidedFromChildren(loadFrameData, updateStepProgress);
        await refreshGuided();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap when estate/session changes
  }, [estateId]);

  useEffect(() => {
    if (!plannerMode && estateId) void refreshGuided();
  }, [location.pathname, estateId, plannerMode, refreshGuided]);

  const persistPlannerData = async (nextCards: PlannerFilingCardRow[]) => {
    if (!estateId || isArchived) return;
    const fd = await loadFrameData(FRAME_ID);
    const prev = (fd?.data || {}) as Record<string, unknown>;
    await saveFrameData(FRAME_ID, {
      ...prev,
      plannerModeUx: true,
      plannerFilings: nextCards,
    });
    setCards(nextCards);
    await syncPlannerFilingCardsToTaxSteps(updateStepProgress, nextCards);
  };

  const startEdit = (row: PlannerFilingCardRow) => {
    setEditingId(row.id);
    setDraft({ ...row });
  };

  const saveEdit = async () => {
    if (!draft?.formTitle?.trim() || !editingId) return;
    const next = cards.map((c) =>
      c.id === editingId ? ({ ...c, ...draft } as PlannerFilingCardRow) : c
    );
    setEditingId(null);
    setDraft(null);
    await persistPlannerData(next);
  };

  const deleteCard = async (id: string) => {
    await persistPlannerData(cards.filter((c) => c.id !== id));
  };

  const togglePlannerComplete = async (id: string) => {
    const next = cards.map((c) => (c.id === id ? { ...c, cardComplete: !c.cardComplete } : c));
    await persistPlannerData(next);
  };

  const onPlannerUpload = async (id: string, fileList: FileList | null) => {
    if (!fileList?.length) return;
    const uploads: PlannerUpload[] = [];
    for (const f of Array.from(fileList)) {
      uploads.push(await loadSmallFilePayload(f));
    }
    const next = cards.map((c) =>
      c.id === id ? { ...c, uploads: [...c.uploads, ...uploads] } : c
    );
    await persistPlannerData(next);
  };

  if (!estateId)
    return <div className="max-w-2xl mx-auto py-12 px-4 text-sm text-stone-600">Select an estate.</div>;

  const pctGuided = Math.round((guidedDone / TAX_GUIDED_TOTAL) * 100);
  const allGuidedDone = guidedDone >= TAX_GUIDED_TOTAL;

  const guidedRows = [
    { title: 'Determine Final Personal Income Tax', frameId: 'sframe-208', to: `/sframe-208/${estateId}` },
    { title: 'Assess Estate Income Tax Requirements', frameId: 'sframe-209', to: `/sframe-209/${estateId}` },
    { title: 'Evaluate Federal Estate (Death) Tax', frameId: 'sframe-210', to: `/sframe-210/${estateId}` },
    { title: 'Identify State-Level Tax Obligations', frameId: 'sframe-212', to: `/sframe-212/${estateId}` },
    {
      title: 'Finalize Tax Calendar & Professional Help',
      frameId: 'sframe-214',
      to: `/sframe-214/${estateId}`,
    },
  ] as const;

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      {error ? (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      ) : null}

      <button
        type="button"
        onClick={() => navigate(`/sframe-198/${estateId}`)}
        className="flex items-center gap-2 text-stone-500 hover:text-stone-700 mb-6 text-sm"
      >
        <ChevronLeft className="w-4 h-4" /> Back to phase overview
      </button>

      <h1 className="text-2xl font-semibold text-stone-800 mb-2">Identify Tax Filings</h1>

      {plannerMode ? (
        <div className="space-y-4 mt-6">
          {cards.map((card) =>
            editingId === card.id && draft ? (
              <div key={card.id} className="rounded-xl border border-cyan-200 bg-cyan-50/30 p-5 space-y-3">
                <label className="text-sm block">
                  <span className="text-stone-500">Form name</span>
                  <input
                    className="mt-1 w-full rounded border border-stone-300 px-2 py-1.5 text-sm"
                    value={draft.formTitle ?? ''}
                    onChange={(e) => setDraft((d) => ({ ...(d ?? {}), formTitle: e.target.value }))}
                  />
                </label>
                <label className="text-sm block">
                  <span className="text-stone-500">Description</span>
                  <textarea
                    className="mt-1 w-full rounded border border-stone-300 px-2 py-1.5 text-sm"
                    rows={3}
                    value={draft.description ?? ''}
                    onChange={(e) => setDraft((d) => ({ ...(d ?? {}), description: e.target.value }))}
                  />
                </label>
                <label className="text-sm block">
                  <span className="text-stone-500">Filing status</span>
                  <input
                    className="mt-1 w-full rounded border border-stone-300 px-2 py-1.5 text-sm"
                    value={draft.filingStatus ?? ''}
                    onChange={(e) => setDraft((d) => ({ ...(d ?? {}), filingStatus: e.target.value }))}
                  />
                </label>
                <label className="text-sm block">
                  <span className="text-stone-500">Summary details</span>
                  <textarea
                    className="mt-1 w-full rounded border border-stone-300 px-2 py-1.5 text-sm"
                    rows={3}
                    value={draft.summary ?? ''}
                    onChange={(e) => setDraft((d) => ({ ...(d ?? {}), summary: e.target.value }))}
                  />
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void saveEdit()}
                    className="px-4 py-2 rounded-lg bg-cyan-700 text-white text-sm"
                  >
                    Save details
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(null);
                      setDraft(null);
                    }}
                    className="px-4 py-2 rounded-lg border border-stone-300 text-sm text-stone-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div key={card.id} className="bg-white rounded-xl border border-stone-200 p-5 space-y-3">
                <div className="flex justify-between gap-3">
                  <div>
                    <h2 className="font-semibold text-stone-900">{card.formTitle}</h2>
                    <p className="text-sm text-stone-600 mt-1">{card.description}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      disabled={isArchived}
                      aria-label="Edit filing"
                      onClick={() => startEdit(card)}
                      className="rounded p-1.5 text-stone-500 hover:bg-stone-50 disabled:opacity-40"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      disabled={isArchived}
                      aria-label="Delete filing"
                      onClick={() => void deleteCard(card.id)}
                      className="rounded p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-40"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="text-sm">
                  <span className="text-stone-500">Filing status: </span>
                  <span className="text-stone-800 font-medium">{card.filingStatus}</span>
                </div>
                {card.summary ? (
                  <p className="text-sm text-stone-600 whitespace-pre-wrap">{card.summary}</p>
                ) : (
                  <p className="text-sm text-stone-400 italic">No summary yet — use Edit details.</p>
                )}
                {card.uploads.length ? (
                  <ul className="text-xs text-stone-600 space-y-0.5">
                    {card.uploads.map((u, i) => (
                      <li key={`${u.name}-${i}`}>{u.name}</li>
                    ))}
                  </ul>
                ) : null}
                <div className="flex flex-wrap items-center gap-4 pt-1">
                  <label className="inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-800 cursor-pointer hover:border-cyan-500 transition-colors">
                    <FileUp className="h-4 w-4 text-cyan-700" aria-hidden />
                    Upload documents
                    <input
                      type="file"
                      multiple
                      className="sr-only"
                      disabled={isArchived}
                      onChange={(e) => void onPlannerUpload(card.id, e.target.files)}
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm text-stone-700">
                    <input
                      type="checkbox"
                      checked={card.cardComplete}
                      disabled={isArchived}
                      onChange={() => void togglePlannerComplete(card.id)}
                      className="h-4 w-4 rounded border-stone-300 text-cyan-600"
                    />
                    Mark form review complete
                  </label>
                </div>
              </div>
            )
          )}
        </div>
      ) : (
        <>
          <div className="mb-6 mt-4">
            <div className="flex justify-between text-sm text-stone-500 mb-1">
              <span>
                {guidedDone} of {TAX_GUIDED_TOTAL} tasks completed
              </span>
              <span>{pctGuided}%</span>
            </div>
            <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
              <div
                className="h-2 bg-cyan-600 rounded-full transition-all"
                style={{ width: `${allGuidedDone ? 100 : pctGuided}%` }}
              />
            </div>
          </div>

          <div className="space-y-3">
            {guidedRows.map((row) => (
              <button
                key={row.to}
                type="button"
                onClick={() => navigate(row.to)}
                className="w-full bg-white rounded-xl border border-stone-200 hover:border-cyan-500 hover:shadow-sm p-5 flex items-center gap-4 text-left transition-all"
              >
                <input
                  type="checkbox"
                  checked={!!guidedRowDone[row.frameId]}
                  readOnly
                  className="h-5 w-5 rounded border-stone-300 text-cyan-600 pointer-events-none"
                />
                <span className="flex-1 font-medium text-stone-800">{row.title}</span>
                <ChevronRight className="w-5 h-5 text-stone-400 shrink-0" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
